/**
 * Seguridad — alta de la verificación en dos pasos (TOTP), `TDS-CORE` DC-09.
 *
 * Máquina de estados de la pantalla:
 *
 *   cargando -> NONE ----[Activar]----> alta (secreto + código) --ok--> códigos
 *            -> PENDING (alta a medias: se ofrece empezar de nuevo)       |
 *            -> ACTIVE <---------------------[Ya los he guardado]---------+
 *
 * Reglas que sostiene:
 *
 * - El secreto y los códigos de recuperación viven SÓLO en el estado de este
 *   componente: nada en localStorage ni en el service worker (ADR-0004). Al
 *   salir de la pantalla se pierden, que es lo correcto.
 * - QR generado en el navegador (ui/CodigoQr.tsx), nunca por un servicio
 *   externo. Además, el enlace otpauth:// para el propio móvil y la clave para
 *   escribirla a mano.
 * - Al confirmar, la API cierra TODAS las sesiones y renueva la cookie de ésta
 *   (account-mfa-service.ts). La pantalla lo dice: el usuario verá que en sus
 *   otros dispositivos le toca volver a entrar.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ESTADO_VALIDACION, type EstadoMfa } from "../api/cliente";
import { t } from "../i18n/es";
import { CodigoQr } from "../ui/CodigoQr";
import { EnlacesAppAutenticador } from "../ui/EnlacesAppAutenticador";
import { Aviso, Boton, Campo, Tarjeta } from "../ui/primitivas";

type Vista =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "error" }
  | { readonly tipo: "estado"; readonly estado: EstadoMfa }
  | { readonly tipo: "alta"; readonly secreto: string; readonly uri: string }
  | { readonly tipo: "codigos"; readonly codigos: readonly string[] };

/** "ABCDEFGH..." -> "ABCD EFGH ...": se copia a mano, y en bloques se equivoca menos. */
function enBloques(secreto: string): string {
  return secreto.replace(/(.{4})/g, "$1 ").trim();
}

export function PantallaSeguridad({
  hayConexion,
  onSesionCaducada,
  enLinea = false,
  onActivada,
}: {
  hayConexion: boolean;
  onSesionCaducada: () => void;
  /**
   * Dentro del paso 3 del asistente del alta (`OD-0039` E-13): sin tarjeta ni
   * explicacion propia, porque el asistente ya dice para que sirve.
   */
  enLinea?: boolean;
  /** Al pulsar «Ya los he guardado»: los codigos ya se han visto. */
  onActivada?: () => void;
}) {
  const [vista, setVista] = useState<Vista>({ tipo: "cargando" });
  const [codigo, setCodigo] = useState("");
  const [errorCodigo, setErrorCodigo] = useState<string | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [copiada, setCopiada] = useState(false);

  const cargar = useCallback(async (): Promise<void> => {
    setVista({ tipo: "cargando" });
    const r = await api.estadoMfa();
    if (r.ok) setVista({ tipo: "estado", estado: r.datos });
    else if (r.estado === 401) onSesionCaducada();
    else setVista({ tipo: "error" });
  }, [onSesionCaducada]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function empezar(): Promise<void> {
    setErrorGeneral(null);
    setOcupado(true);
    const r = await api.empezarMfa();
    setOcupado(false);
    if (r.ok) {
      setCodigo("");
      setErrorCodigo(null);
      setCopiada(false);
      setVista({ tipo: "alta", secreto: r.datos.secreto, uri: r.datos.uri });
    } else if (r.estado === 401) onSesionCaducada();
    else if (r.estado === 409) {
      setErrorGeneral(t.seguridad.yaActiva);
      void cargar();
    } else setErrorGeneral(r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico);
  }

  async function confirmar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErrorGeneral(null);
    const limpio = codigo.replace(/\s/g, "");
    if (!/^\d{6}$/.test(limpio)) {
      setErrorCodigo(t.seguridad.faltaCodigo);
      return;
    }
    setErrorCodigo(null);
    setOcupado(true);
    const r = await api.confirmarMfa(limpio);
    setOcupado(false);
    if (r.ok) {
      setCodigo("");
      setVista({ tipo: "codigos", codigos: r.datos });
    } else if (r.estado === 401) onSesionCaducada();
    else if (r.estado === ESTADO_VALIDACION && r.error !== null) setErrorCodigo(r.error.message);
    else if (r.estado === 409) void cargar();
    else setErrorGeneral(r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico);
  }

  async function copiar(secreto: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(secreto);
      setCopiada(true);
    } catch {
      // Sin permiso de portapapeles la clave sigue visible para copiarla a mano.
    }
  }

  const contenido = (
    <>
      {vista.tipo === "cargando" ? <p>{t.seguridad.cargando}</p> : null}

      {vista.tipo === "error" ? (
        <>
          <Aviso tono="error">{t.seguridad.error}</Aviso>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.seguridad.reintentar}
          </Boton>
        </>
      ) : null}

      {vista.tipo === "estado" && vista.estado.totp === "ACTIVE" ? (
        <Aviso tono="exito" titulo={t.seguridad.activa}>
          {t.seguridad.activaDetalle(vista.estado.codigosRestantes)}
        </Aviso>
      ) : null}

      {vista.tipo === "estado" && vista.estado.totp !== "ACTIVE" ? (
        <>
          {enLinea ? null : <p>{t.seguridad.inactiva}</p>}
          <EnlacesAppAutenticador />
          <Boton onClick={() => void empezar()} deshabilitado={ocupado || !hayConexion}>
            {ocupado ? t.seguridad.activando : t.seguridad.activar}
          </Boton>
        </>
      ) : null}

      {vista.tipo === "alta" ? (
        <>
          <p>{t.seguridad.paso1}</p>
          <EnlacesAppAutenticador />
          <CodigoQr contenido={vista.uri} etiqueta={t.seguridad.qr} />
          <a className="boton boton-secundario enlace-boton" href={vista.uri}>
            {t.seguridad.abrirEnApp}
          </a>
          <div>
            <p className="ayuda-neutra">{t.seguridad.claveManual}</p>
            <p className="clave-secreta">{enBloques(vista.secreto)}</p>
          </div>
          <Boton variante="secundario" onClick={() => void copiar(vista.secreto)}>
            {copiada ? t.seguridad.copiada : t.seguridad.copiar}
          </Boton>
          <form onSubmit={confirmar} noValidate>
            <p>{t.seguridad.paso2}</p>
            <Campo
              id="mfa-codigo"
              etiqueta={t.seguridad.codigo}
              valor={codigo}
              onChange={setCodigo}
              inputMode="numeric"
              autoComplete="one-time-code"
              error={errorCodigo}
            />
            <Boton tipo="submit" deshabilitado={ocupado || !hayConexion}>
              {ocupado ? t.seguridad.confirmando : t.seguridad.confirmar}
            </Boton>
          </form>
          <Boton variante="secundario" onClick={() => void cargar()}>
            {t.seguridad.cancelar}
          </Boton>
        </>
      ) : null}

      {vista.tipo === "codigos" ? (
        <>
          <Aviso tono="aviso" titulo={t.seguridad.codigosTitulo}>
            {t.seguridad.codigosDetalle}
          </Aviso>
          <ol className="codigos-recuperacion">
            {vista.codigos.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ol>
          <Aviso tono="info">{t.seguridad.codigosAviso}</Aviso>
          <Boton
            onClick={() => {
              void cargar();
              onActivada?.();
            }}
          >
            {t.seguridad.guardados}
          </Boton>
        </>
      ) : null}

      {errorGeneral !== null ? <Aviso tono="error">{errorGeneral}</Aviso> : null}
    </>
  );

  if (enLinea) return <div className="seguridad-en-linea">{contenido}</div>;
  return <Tarjeta titulo={t.seguridad.titulo}>{contenido}</Tarjeta>;
}
